// ВАЖНО: polyfills — САМЫЙ ПЕРВЫЙ импорт. Чинит Object.hasOwn на старых
// webview ДО того, как что-либо (включая armWatch → ./lib/tg.js →
// @telegram-apps/*) транзитивно вызовет его и словит белый экран
// (см. polyfills.ts).
import './boot/polyfills.js';
// ВАЖНО: armWatch — вторым импортом. Он вооружает mini-watch (слушатели
// 'error'/'unhandledrejection') ещё до вычисления любого нашего кода ниже,
// включая `import { env }` с его zod fail-fast. Иначе ранние ошибки бута/
// конфигурации некому ловить (см. armWatch.ts).
import './boot/armWatch.js';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { App } from './App.js';
import { bootSdk } from './boot/bootSdk.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { NotInTelegramScreen } from './components/NotInTelegramScreen.js';
import { env } from './env.js';
import {
  applyMockTelegramEnv,
  type MockPlatform,
  type MockTheme,
} from './lib/mock.js';
import { installMockApi } from './lib/mockApi.js';
// Стили кита @telegram-apps/telegram-ui ДО наших — наш styles.css может
// переопределять (путь сверен по package.json пакета, Веха 13.0: нет exports,
// css лежит как dist/styles.css).
import '@telegram-apps/telegram-ui/dist/styles.css';
import './styles.css';

/**
 * Entry point @veylme/miniapp.
 *
 * Порядок:
 *   1. Импорт `env` уже триггернул zod-parse (fail-fast при невалидной конфигурации,
 *      CANON SP1; production-guard на VITE_USE_TG_MOCK см. env.ts).
 *   2. Если включён mock (`VITE_USE_TG_MOCK=true`) — `applyMockTelegramEnv()` ДО
 *      `bootSdk()`. Иначе SDK 3.x кинет `LaunchParamsRetrieveError`.
 *   3. `bootSdk()` — подъём SDK (init → CSS-vars темы → viewport), целиком в
 *      try/catch. Возвращает 'ready' | 'unavailable' и НИКОГДА не бросает:
 *      раньше `initSDK()` стоял прямо здесь, в теле модуля, и вне Telegram его
 *      исключение обрывало модуль ДО `createRoot(...)` — React не монтировался,
 *      человек видел белый экран (work-039). См. boot/bootSdk.ts.
 *   4. Render `<StrictMode><ErrorBoundary>…</ErrorBoundary></StrictMode>`, где
 *      внутри либо `<App/>` ('ready'), либо `<NotInTelegramScreen/>`
 *      ('unavailable' — честный текст вместо белизны).
 *      `ErrorBoundary` (аудит P2.1/MF-1) остаётся вторым рубежом: ловит любое
 *      падение рендера уже смонтированного дерева и ре-репортит ошибку в
 *      mini-watch (см. components/ErrorBoundary.tsx). Внутри StrictMode —
 *      чтобы ловить и ошибки, которые StrictMode провоцирует двойным рендером.
 */

if (env.VITE_USE_TG_MOCK) {
  // Платформу/тему для dev можно задать через query: ?tgPlatform=ios&tgTheme=light
  // (Веха 13.1 — проверка нативных форм iOS/Android/Desktop без сборки под Telegram).
  const q = new URLSearchParams(window.location.search);
  const platform = q.get('tgPlatform') ?? undefined;
  const theme = q.get('tgTheme') ?? undefined;
  const premium = q.get('tgPremium') === 'true';
  // Dev-affordance (work-020): `?tgStartParam=box_mocktoken1234` открывает ASK-экран
  // (вне Telegram start_param взять негде). Парс → tgWebAppStartParam в mock env.
  const startParam = q.get('tgStartParam') ?? undefined;
  applyMockTelegramEnv({
    platform: platform as MockPlatform | undefined,
    theme: theme as MockTheme | undefined,
    premium,
    startParam,
  });
  // Перехват fetch → фикстуры (экраны иначе ушли бы на реальный сервер с
  // фейковым initData). Только под VITE_USE_TG_MOCK; см. lib/mockApi.ts.
  installMockApi();
}

// mini-watch уже вооружён в ./boot/armWatch.js (ПЕРВЫМ импортом, до парса env) —
// чтобы ловить даже ранние ошибки конфигурации/бута. Здесь повторно не поднимаем.

// SDK живёт всю жизнь страницы (в Mini App нет момента unmount окна), поэтому
// cleanup не сохраняем. Подъём — целиком внутри bootSdk(), которая не бросает:
// падение SDK не должно помешать смонтировать React (work-039).
const sdk = bootSdk();

const root = document.getElementById('root');
if (!root) throw new Error('Root element #root not found');

createRoot(root).render(
  <StrictMode>
    <ErrorBoundary>
      {sdk === 'ready' ? <App /> : <NotInTelegramScreen />}
    </ErrorBoundary>
  </StrictMode>,
);
